<script lang="ts" setup>
import ScheduledPlanChangeMessage from "./ScheduledPlanChangeMessage.vue";
import { computed, ref } from "vue";
import { format, parse } from "date-fns";
import { checkoutApi } from "./requests";
import { frequencies, planLabels } from "./types";
import { useUserStore } from "@/stores/userStore";
import { useIsLoading } from "@/components/ui/loaders/loadingStore";
import { getApiErrorMessage } from "@/api/errors";

const userStore = useUserStore();
const isLoading = useIsLoading();

const errorMessage = ref<string | null>(null);

const onCancelClicked = async () => {
    isLoading.value = true;
    errorMessage.value = null;
    try {
        await checkoutApi.cancelSubscriptionChange();
        await userStore.getUsersSubscriptions();
    } catch (e) {
        errorMessage.value = getApiErrorMessage(e);
    } finally {
        isLoading.value = false;
    }
};

const props = defineProps<{
    schedule: any;
}>();

const getMetadata = (key: string) => {
    return props.schedule?.metadata[key];
};

const hasScheduledChanges = computed(() => {
    return props.schedule;
});

const dateChange = computed(() => {
    let dateUnixTime = props.schedule?.phases?.[1]?.start_date;
    let date = parse(dateUnixTime, "t", new Date());
    return format(date, "MMM dd, yyyy");
});

const frequencyCurrent = computed(() => {
    return getMetadata("frequency_current") || "";
});

const frequencyNew = computed(() => {
    return getMetadata("frequency_new") || "";
});

const planCurrent = computed(() => {
    return getMetadata("plan_current") || "";
});

const planNew = computed(() => {
    return getMetadata("plan_new") || "";
});
const newPriceCurrency = computed(() => {
    return (
        props.schedule?.phases?.[1]?.items?.[0]?.price?.currency || "usd"
    ).toUpperCase();
});

const newPrice = computed(() => {
    const amount =
        props.schedule?.phases?.[1]?.items?.[0]?.price?.unit_amount || 0;
    return (amount / 100).toFixed(2);
});

const changeMessage = computed(() => {
    // month, quarter, year
    let frequency = frequencies[frequencyNew.value];
    let planText = planLabels[planNew.value];
    // Example: Your new billing frequency is monthly effective on Mar 21, 2023.
    return `Your change to <strong>${planText}</strong> plan billed <strong>${frequency.alt}</strong> will be effective
        ${dateChange.value}.`; //The new price will be $${newPrice.value} ${frequency.per}.`;
});
</script>
<template>
    <ScheduledPlanChangeMessage v-if="hasScheduledChanges">
        <template #message>
            <!-- safe: changeMessage is computed from planLabels/frequencies lookup tables (internal enum data), not user input -->
            <p class="text-sm" v-html="changeMessage"></p>
            <p v-if="errorMessage" role="alert" class="text-sm text-red-700">
                We couldn't cancel your plan change. {{ errorMessage }}
            </p>
        </template>
        <template #action>
            <button
                class="text-blue-500 text-sm hover:cursor-pointer"
                @click="onCancelClicked"
            >
                Cancel Change
            </button>
        </template>
    </ScheduledPlanChangeMessage>
</template>
